import { Component, Input, OnInit, Output, EventEmitter } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { DepartmentUser } from "../../../model/department-user";
import { DepartmentUserService } from "../../../services/department-user.service";
import { AuthService } from "../../../auth.service";
import { Router } from "@angular/router";
import { NavigationHelper } from "../../../services/navigation.helper";
import { UsersService } from "../../../services/users.service";
import { BehaviorSubject, Observable, Observer } from "rxjs";
import { Navigation } from "../../../model/navigation";
import { ActionButtonSubmit } from "../../../model/action-button-submit";
import { Messages } from "../../../model/messages";
import { DepartmentsService } from "../../../services/departaments.service";
import { Labeler } from "../../../model/labeler";
import { SingleChoice } from "../../../model/singlechoice";
import { Department } from "../../../model/departments";

@Component({
  selector: "department-user-details",
  templateUrl: "./department-user-details.component.html",
  styleUrls: ["./department-user-details.component.css"],
})
export class DepartmentUserDetailsComponent implements OnInit {
  messages = new BehaviorSubject<Messages>(Messages.EMPTY);
  departmenUser = new BehaviorSubject<DepartmentUser>(
    new DepartmentUser(0, 0, 0)
  );
  editMode: boolean = false;
  departmentUserForm: FormGroup;
  departmentUserTitle: string =
    "departmentUserDetails.title.department.user.details";
  @Output() choiceEvent: EventEmitter<SingleChoice> = new EventEmitter();
  @Input() currentDepartment: Observable<Department>;
  userIdLabeler = new BehaviorSubject<string>("-");

  departmenUsers: BehaviorSubject<DepartmentUser[]> = new BehaviorSubject<
    DepartmentUser[]
  >([]);
  isCreating: boolean = true;
  completionUrl = new BehaviorSubject<string>("");
  isLoading: boolean = false;
  stateGuard: boolean = false;
  @Output() formClosed = new EventEmitter<void>();
  showSaveButton: boolean = true;
  department: Department | null = null;
  constructor(
    private fb: FormBuilder,
    private departmentUserService: DepartmentUserService,
    private auth: AuthService,
    private router: Router,
    private nav: NavigationHelper,
    private users: UsersService,
    private departmentService: DepartmentsService
  ) {}

  ngOnInit(): void {
    this.departmentUserService.departmentUserObservable.subscribe(
      (departmenUser) => {
        this.departmenUsers.next(departmenUser);
      }
    );

    this.initializeForm();

    const restoredState = this.departmentUserService.restoreFormState();

    this.departmentUserForm.valueChanges.subscribe((formValue) => {
      if (this.stateGuard) {
      } else if (this.editMode) {
        this.stateGuard = true;

        this.departmentUserService.setFormState(formValue);
        this.stateGuard = false;
      }
    });
    this.departmentUserService.formState.subscribe((restoredState) => {
      if (this.stateGuard) {
      } else if (this.editMode) {
        this.restoreFormValues(restoredState);
      }
    });
    this.departmenUser.subscribe((departmenUser) => {
      this.userIdLabeler.next(this.userLabel);
    });

    this.departmentUserService.selectedDepartmentUserObservable.subscribe(
      (selectedDepartmentUser) => {
        const action = this.nav.currentAction;
        if ("fragmentedit" === action && selectedDepartmentUser) {
          const oldGuard = this.stateGuard;
          this.stateGuard = true;
          const restored = this.departmentUserService.restoreFormState();
          if (restored) {
            if (restored.userId) {
              selectedDepartmentUser.userId = restored.userId;
            }
            if (restored.departmentId) {
              selectedDepartmentUser.departmentId = restored.departmentId;
            }
          }
          if (
            selectedDepartmentUser === this.departmenUser.value ||
            selectedDepartmentUser.userId === this.departmenUser.value.userId
          ) {
            this.departmenUser.next(selectedDepartmentUser);
          }

          this.populateForm(selectedDepartmentUser);
        }
      }
    );

    this.nav.topic.subscribe((topic) => {
      this.adaptToNavigation();
    });
    this.adaptToNavigation();
  }

  adaptToNavigation(): void {
    const current = this.nav.current;
    if (
      "departments" !== current.topic ||
      "department-user" !== current.fragmentTopic
    ) {
      return;
    }

    if (Navigation.NEW.toString() === current.fragmentId) {
      this.editMode = true;

      this.departmentUserTitle =
        "departmentUserDetails.title.new.department.user";

      this.completionUrl.next(
        "departments/" +
          current.id +
          "/fragmentedit/department-user/" +
          Navigation.NEW
      );
      return;
    }

    if (current.action === "fragmentedit") {
      this.editMode = true;
      this.departmentUserTitle =
        "departmentUserDetails.title.edit.department.user";
    } else {
      this.editMode = false;
      this.departmentUserTitle =
        "departmentUserDetails.title.department.user.details";
    }

    const currentUserId = Number(current.fragmentId);
    if (currentUserId > 0) {
      const selectedUser = this.departmentUserService.currentDepartmentUser;

      const selectedUserId = selectedUser?.userId;

      if (selectedUserId && selectedUserId === currentUserId) {
      } else {
        const token = this.auth.token;

        this.departmentUserService
          .getById(token, currentUserId)
          .subscribe((messages) => {
            if (messages.hasAnyMessages) {
              this.messages.next(messages);
            } else {
              const loadedUser =
                this.departmentUserService.currentDepartmentUser;
            }
          });
      }
    }
  }

  restoreFormValues(restoredState: Partial<DepartmentUser>) {
    if (Object.keys(restoredState).length > 0) {
      this.departmentUserForm.patchValue(restoredState, { emitEvent: false });
    }
  }

  private initializeForm(): void {
    this.departmentUserForm = this.fb.group({
      departmentId: 0,
      userId: 0,
    });
  }

  private populateForm(departmentUser: DepartmentUser): void {
    if (
      this.departmentUserForm.value.userId !== departmentUser.userId ||
      this.departmentUserForm.value.departmentId !== departmentUser.departmentId
    ) {
      this.departmentUserForm.patchValue({
        departmentId: departmentUser.departmentId,
        userId: departmentUser.userId,
      });
    }

    this.editMode = departmentUser.userId === Number(Navigation.NEW);

    this.departmentUserTitle = this.editMode
      ? "departmentUserDetails.title.edit.department.user"
      : "departmentUserDetails.title.department.user.details";
  }

  saveDepartmentUser(action: ActionButtonSubmit): void {
    this.isLoading = true;
    this.formClosed.emit();

    if (this.departmentUserForm.valid) {
      const token = this.auth.token;
      const current = this.nav.current;
      const departmentId = Number(current.id);

      const formValue = this.departmentUserForm.value;

      const createdDepartmentUser = {
        id: this.departmenUser.value.id ?? Navigation.NEW,
        departmentId: Number(this.nav.current.id),
        userId: formValue.userId,
      };

      this.departmentUserService
        .createDepartmentUser(token, createdDepartmentUser)
        .subscribe((messages) => {
          if (messages.hasAnyMessages) {
            this.messages.next(messages);
          } else {
            const departmentUserId =
              this.departmentUserService.currentDepartmentUser.id;
            this.updatedDepartmentUserAfterSave(
              messages,
              action,
              departmentId,
              departmentUserId,
              token
            );
          }
        });
    } else {
    }
  }

  updatedDepartmentUserAfterSave(
    messages: Messages,
    action: ActionButtonSubmit,
    departmentId: number,
    departmentUserId: number,
    token: string
  ) {
    this.isLoading = false;
    this.messages.next(messages);
    if (messages.hasAnyMessages) {
      action.result(false);
    } else {
      action.result(true);

      this.departmentService
        .getDepartmentsById(token, departmentId)

        .subscribe((messages) => {
          if (messages.hasAnyMessages) {
            this.messages.next(messages);
          } else {
            const url =
              "departments/" + departmentId + "/fragment/department-user/";

            this.router.navigate([url]);

            this.departmentUserService.clearFormStateBackup();
          }
        });
    }
  }
  relaySingleChoice(choice: SingleChoice) {
    this.showSaveButton = true;
    this.nav.startChoice(choice);
  }

  userIdObserver: Partial<Observer<number>> = {
    next: (value: number) => {
      this.departmentUserForm.patchValue({ userId: value });
      this.departmentUserService.setFormState(this.departmentUserForm.value);
    },
  };

  onUserIdChoice(departmentUserId: number): void {
    const current = this.departmenUser.value;
    current.userId = departmentUserId;
    this.departmenUser.next(current);
    this.departmentUserForm.patchValue({ userId: departmentUserId });
  }

  get userId(): number {
    const departmenUser = this.departmenUser.value;
    return departmenUser && departmenUser.userId ? departmenUser.userId : -1;
  }

  get departmenUserId(): number {
    const departmenUser = this.departmenUser.value;
    return departmenUser && departmenUser.userId
      ? departmenUser.userId
      : Navigation.NEW;
  }
  get userLabel(): string {
    const idStr = this.userId;
    const id = Number(idStr);
    let label = "-";
    if (!isNaN(id)) {
      label = this.users.labelForId(id);
    }
    return label;
  }

  get TRUE(): boolean {
    return true;
  }
  get FALSE(): boolean {
    return false;
  }
  get userIdLabelerByService(): Labeler {
    return this.users;
  }
}
